import React, { useCallback, useEffect, useRef, useState } from "react"
import { motion, AnimatePresence } from "framer-motion"
import useFlashMessages from "hooks/useFlashMessages"
import Dialog from "components/Dialog"
import * as styles from "./index.module.css"
import { flags } from "lib/environment"

const FlashMessages = () => {
  const { data, dismissFlashMessage, resetOldDismissedMessages } =
    useFlashMessages()

  const [showModal, setShowModal] = useState(false)
  const [disableConfirmation, setDisableConfirmation] = useState(false)
  const contentRef = useRef<HTMLDivElement>(null)
  const titleRef = useRef<HTMLDivElement>(null)
  const current = data?.results.find(
    message => message.displayType === `modal` && !message.dismissed
  )

  const onConfirmation = () => {
    if (!data?.results.length || typeof current === `undefined`) return

    dismissFlashMessage({ id: current.id })
  }

  const onClose = useCallback(() => {
    if (!data?.results.length || typeof current === `undefined`) return
    // Close the modal but don't write the dissmissal state to DB. Messages will have dismissal state reset so they load again on next page load (local storage cleared in gatsby-browser)
    dismissFlashMessage({ id: current.id, soft: true })
  }, [current, data?.results.length, dismissFlashMessage])

  useEffect(() => {
    if (flags.SOFT_FLASH_MESSAGE_DISMISSAL) {
      resetOldDismissedMessages()
    }
  }, [resetOldDismissedMessages])

  useEffect(() => {
    if (data?.results?.length && current) {
      setShowModal(true)
    } else {
      setShowModal(false)
    }
  }, [current, data?.results?.length])

  if (!current) {
    return null
  }

  return (
    <Dialog
      open={showModal}
      title={
        <AnimatePresence mode="wait">
          <motion.div
            key={current.id}
            layout
            initial={{
              y: 10,
              opacity: 0,
              height: titleRef.current?.offsetHeight
            }}
            animate={{ y: 0, opacity: 1, height: `auto` }}
            exit={{
              y: -10,
              opacity: 0,
              height: `auto`
            }}
            transition={{ duration: 0.2 }}
          >
            <div ref={titleRef} data-testid="flash-message-title">
              {current.title}
            </div>
          </motion.div>
        </AnimatePresence>
      }
      onClose={onClose}
      confirmationText="I've read this"
      confirmationDisabled={disableConfirmation}
      onConfirmation={onConfirmation}
      cancelText=""
      closable={flags.SOFT_FLASH_MESSAGE_DISMISSAL}
    >
      <AnimatePresence mode="wait">
        <motion.div
          key={current.id}
          layout
          initial={{
            y: 10,
            opacity: 0,
            height: contentRef.current?.offsetHeight
          }}
          animate={{ y: 0, opacity: 1, height: `auto` }}
          exit={{
            y: -10,
            opacity: 0,
            height: `auto`
          }}
          transition={{ duration: 0.2 }}
          onAnimationStart={() => setDisableConfirmation(true)}
          onAnimationComplete={() => setDisableConfirmation(false)}
        >
          <div
            ref={contentRef}
            className={styles.content}
            dangerouslySetInnerHTML={{
              // Most of the messages seem to have a trailing double break,
              // which looks ugly so we're just gonna strip it out
              __html: current.message.replace(/<br><br>$/, ``)
            }}
            data-testid="flash-message-content"
          />
        </motion.div>
      </AnimatePresence>
    </Dialog>
  )
}

export default FlashMessages
